import { useMemo, useState, useCallback } from "react"
import { useReadableStampCards, useReadableStampMissions, useUserStampCardStates, useUserStampMissionProgresses, useUserStampMissionDailyProgresses, useStampLanes, useStampLaneRewards, useUserStampRewardClaims } from "popopo-lib-front/firestore-access"
import { StampCardDocument, StampCardId, StampMissionDocument, UserId, UserStampCardStateDocument, UserStampMissionProgressDocument, getProgressKindFromMissionKind, getRepeatKindFromMissionKind, getTargetCountFromMission, StampLaneDocument, StampLaneRewardDocument, StampLaneId, UserStampRewardClaimDocument, isSuperLane, UserStampCardStateSuperLaneUnlockByCoin, allMissionKinds, isStampMissionCompleted } from "popopo-msg"
import type { MissionTimeScope } from "popopo-msg"
import { sortBy, calculateBusinessDay, assertNever } from "popopo-lib-common"
import { useToast } from "popopo-lib-front/hook"
import { api, firestore } from "../../core"
import { SpinnerRow } from "../SpinnerRow"
import { useAccessContext } from "../guard/AccessGuard"
import { formatDateTime } from "../../lib/util"

type LimitedMissionProgressDisplayProps = {
  mission: StampMissionDocument
  progress: UserStampMissionProgressDocument | undefined
}

const LimitedMissionProgressDisplay = ({ mission, progress }: LimitedMissionProgressDisplayProps) => {
  const progressKind = getProgressKindFromMissionKind(mission.kind)

  if (progressKind === "boolean") {
    const completed = progress && "complete_status" in progress && isStampMissionCompleted(progress.complete_status)
    return (
      <span className={`badge ${completed ? "bg-success" : "bg-secondary"}`}>
        {completed ? "達成済み" : "未達成"}
      </span>
    )
  }

  if (progressKind === "counter") {
    const currentCount = progress && "progress_count" in progress ? (progress.progress_count ?? 0) : 0
    const targetCount = getTargetCountFromMission(mission)
    return (
      <span className="text-break">
        {currentCount} / {targetCount}
        {targetCount <= currentCount && (
          <span className="badge bg-success ms-2">達成済み</span>
        )}
      </span>
    )
  }

  return <span>-</span>
}

const daysBetweenDayKeys = (startDayKey: string, endDayKey: string): number => {
  const start = new Date(startDayKey + "T00:00:00Z")
  const end = new Date(endDayKey + "T00:00:00Z")
  return Math.max(1, Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1)
}

type DailyMissionProgressDisplayProps = {
  mission: StampMissionDocument
  userId: UserId
  cardId: StampCardId
  missionPeriodStartAt: number
}

const DailyMissionProgressDisplay = ({ mission, userId, cardId, missionPeriodStartAt }: DailyMissionProgressDisplayProps) => {
  const dailyProgresses = useUserStampMissionDailyProgresses(firestore, userId, cardId, mission.id)
  const progressKind = getProgressKindFromMissionKind(mission.kind)

  if (dailyProgresses === "loading") {
    return <SpinnerRow />
  }

  const { dayKey } = calculateBusinessDay(new Date())
  const todayProgress = dailyProgresses.find(p => p.day_key === dayKey)
  const startDayKey = calculateBusinessDay(new Date(missionPeriodStartAt)).dayKey
  const elapsedDays = daysBetweenDayKeys(startDayKey, dayKey)
  const targetCount = getTargetCountFromMission(mission)
  const achievedDays = dailyProgresses.filter(p =>
    progressKind === "boolean" ? isStampMissionCompleted(p.complete_status) : targetCount <= (p.progress_count ?? 0)
  ).length

  if (progressKind === "boolean") {
    const completed = todayProgress ? isStampMissionCompleted(todayProgress.complete_status) : false
    return (
      <span className="text-break">
        <span className={`badge ${completed ? "bg-success" : "bg-secondary"}`}>
          {dayKey} {completed ? "達成済み" : "未達成"}
        </span>
          <span className="ms-2 small text-muted">({elapsedDays}日中{achievedDays}日達成)</span>
        </span>
      )
    }

    if (progressKind === "counter") {
      const currentCount = todayProgress?.progress_count ?? 0
      const todayCompleted = targetCount <= currentCount
      return (
        <span className="text-break">
          <span className={`badge ${todayCompleted ? "bg-success" : "bg-secondary"}`}>
            {dayKey} {currentCount}/{targetCount}
          </span>
          <span className="ms-2 small text-muted">({elapsedDays}日中{achievedDays}日達成)</span>
      </span>
    )
  }

  return <span>-</span>
}

type MissionProgressDisplayProps = {
  mission: StampMissionDocument
  progress: UserStampMissionProgressDocument | undefined
  userId: UserId
  cardId: StampCardId
  missionPeriodStartAt: number
}

export const MissionProgressDisplay = ({ mission, progress, userId, cardId, missionPeriodStartAt }: MissionProgressDisplayProps) => {
  const repeatKind = getRepeatKindFromMissionKind(mission.kind)

  if (repeatKind === "daily") {
    return <DailyMissionProgressDisplay mission={mission} userId={userId} cardId={cardId} missionPeriodStartAt={missionPeriodStartAt} />
  }

  return <LimitedMissionProgressDisplay mission={mission} progress={progress} />
}

type StampLaneRewardItemProps = {
  cardId: StampCardId
  laneId: StampLaneId
  reward: StampLaneRewardDocument
  isClaimed: boolean
  currentStampCount: number
}

const COINS_PER_STAMP = 20

const StampLaneRewardItem = ({ cardId, laneId, reward, isClaimed, currentStampCount }: StampLaneRewardItemProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const toast = useToast()
  const context = useAccessContext()

  const shortage = Math.max(0, reward.threshold_stamps - currentStampCount)
  const coinsNeeded = shortage * COINS_PER_STAMP

  const handleClaim = useCallback(async () => {
    if (isClaimed || isSubmitting) return

    setIsSubmitting(true)

    try {
      const idToken = await context.credential.user.getIdToken()
      const response = await api.POST(
        "/api/v2/stamp-cards/{card_id}/stamp-lanes/{lane_id}/stamp-lane-rewards/{reward_id}/stamp-reward-claims",
        {
          params: {
            path: {
              card_id: cardId,
              lane_id: laneId,
              reward_id: reward.id,
            },
          },
          headers: {
            authorization: `Bearer ${idToken}`,
          },
        }
      )

      if (response.error) {
        const errorMessage = response.error.message ?? "報酬の受け取りに失敗しました"
        toast.add({ type: "danger", title: "報酬受け取り", message: errorMessage })
      } else {
        toast.add({ type: "success", title: "報酬受け取り", message: "報酬を受け取りました" })
      }
    } catch {
      toast.add({ type: "danger", title: "報酬受け取り", message: "報酬の受け取りに失敗しました" })
    } finally {
      setIsSubmitting(false)
    }
  }, [isClaimed, isSubmitting, cardId, laneId, reward.id, toast, context.credential.user])

  const handlePurchase = useCallback(async () => {
    if (isPurchasing || shortage <= 0) return

    setIsPurchasing(true)

    try {
      const idToken = await context.credential.user.getIdToken()
      const response = await api.POST(
        "/api/v2/stamp-cards/{card_id}/stamps",
        {
          params: {
            path: {
              card_id: cardId,
            },
            header: {
              // TODO: 仮の値。Webからのリクエストとして適切な値に変更する必要があります
              "X-Requested-With": "android",
            },
          },
          headers: {
            authorization: `Bearer ${idToken}`,
          },
          body: {
            kind: "purchase",
            laneId: laneId,
            rewardId: reward.id,
            stampCount: shortage,
          },
        }
      )

      if (response.error) {
        const errorMessage = response.error.message ?? "スタンプの購入に失敗しました"
        toast.add({ type: "danger", title: "スタンプ購入", message: errorMessage })
      } else {
        toast.add({ type: "success", title: "スタンプ購入", message: `${shortage}スタンプを${coinsNeeded}コインで購入しました` })
      }
    } catch {
      toast.add({ type: "danger", title: "スタンプ購入", message: "スタンプの購入に失敗しました" })
    } finally {
      setIsPurchasing(false)
    }
  }, [isPurchasing, shortage, cardId, laneId, reward.id, coinsNeeded, toast, context.credential.user])

  const rewardDescription = reward.kind === "item"
    ? `アイテム: ${reward.item.item_name_snapshot}`
    : `コイン: ${reward.coin.amount}`

  return (
    <li className="list-group-item px-0">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          <div className="small text-muted">
            {reward.threshold_stamps}スタンプ達成報酬
          </div>
          <div className="fw-bold">{rewardDescription}</div>
        </div>
        <div className="flex-shrink-0 d-flex flex-column gap-2">
          {isClaimed ? (
            <span className="badge bg-secondary">受取済み</span>
          ) : (
            <>
              {0 < shortage && (
                <button
                  className="btn btn-warning btn-sm text-wrap"
                  onClick={handlePurchase}
                  disabled={isPurchasing}
                >
                  {isPurchasing ? "処理中..." : `BUY ${shortage} (${coinsNeeded}coin)`}
                </button>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={handleClaim}
                disabled={isSubmitting}
              >
                {isSubmitting ? "処理中..." : "受け取る"}
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  )
}

const formatDateOrIndefinite = (timestamp: number | null | undefined): string => {
  if (timestamp == null) return "無期限"
  return formatDateTime(timestamp)
}

function getMissionKindLabel(kind: string): string {
  const missionKind = allMissionKinds.find(m => m.value === kind)
  return missionKind?.label ?? kind
}

function getTimeScopeLabel(timeScope: MissionTimeScope): string {
  switch (timeScope) {
    case "retrospective":
      return "これまで"
    case "since_mission_activated":
      return "これから"
    default:
      return assertNever(timeScope)
  }
}

type BulkClaimButtonProps = {
  cardId: StampCardId
}

const BulkClaimButton = ({ cardId }: BulkClaimButtonProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useToast()
  const context = useAccessContext()

  const handleBulkClaim = useCallback(async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const idToken = await context.credential.user.getIdToken()
      const response = await api.POST(
        "/api/v2/stamp-cards/{card_id}/stamp-reward-claims",
        {
          params: {
            path: {
              card_id: cardId,
            },
          },
          headers: {
            authorization: `Bearer ${idToken}`,
          },
        }
      )

      if (response.error) {
        const errorMessage = response.error.message ?? "報酬の一括受け取りに失敗しました"
        toast.add({ type: "danger", title: "一括受取", message: errorMessage })
      } else {
        const claimsCount = response.data?.claims?.length ?? 0
        const isPartialSuccess = response.response.status === 207
        if (isPartialSuccess) {
          toast.add({ type: "warning", title: "一括受取", message: `${claimsCount}件の報酬を受け取りましたが、一部の報酬の受け取りに失敗しました` })
        } else {
          toast.add({ type: "success", title: "一括受取", message: `${claimsCount}件の報酬を受け取りました` })
        }
      }
    } catch {
      toast.add({ type: "danger", title: "一括受取", message: "報酬の一括受け取りに失敗しました" })
    } finally {
      setIsSubmitting(false)
    }
  }, [isSubmitting, cardId, toast, context.credential.user])

  return (
    <button
      className="btn btn-success btn-sm"
      onClick={handleBulkClaim}
      disabled={isSubmitting}
    >
      {isSubmitting ? "処理中..." : "報酬一括受取"}
    </button>
  )
}

type SuperLaneUnlockButtonProps = {
  cardId: StampCardId
  laneId: StampLaneId
  unlockCoinPrice: number
  superLaneUnlockByCoin: UserStampCardStateSuperLaneUnlockByCoin | undefined
}

const SuperLaneUnlockButton = ({ cardId, laneId, unlockCoinPrice, superLaneUnlockByCoin }: SuperLaneUnlockButtonProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useToast()
  const context = useAccessContext()

  const handleUnlock = useCallback(async () => {
    if (isSubmitting || superLaneUnlockByCoin) return

    setIsSubmitting(true)

    try {
      const idToken = await context.credential.user.getIdToken()
      const response = await api.POST(
        "/api/v2/stamp-cards/{card_id}/stamp-lanes/{lane_id}/unlock",
        {
          params: {
            path: {
              card_id: cardId,
              lane_id: laneId,
            },
            header: {
              "X-Requested-With": "android",
            },
          },
          headers: {
            authorization: `Bearer ${idToken}`,
          },
          body: {
            unlockBy: "coin",
            coinPrice: unlockCoinPrice,
          },
        }
      )

      if (response.error) {
        const errorMessage = response.error.message ?? "スーパーレーンの解放に失敗しました"
        toast.add({ type: "danger", title: "スーパーレーン解放", message: errorMessage })
      } else {
        toast.add({ type: "success", title: "スーパーレーン解放", message: `スーパーレーンを${unlockCoinPrice}コインで解放しました` })
      }
    } catch {
      toast.add({ type: "danger", title: "スーパーレーン解放", message: "スーパーレーンの解放に失敗しました" })
    } finally {
      setIsSubmitting(false)
    }
  }, [isSubmitting, superLaneUnlockByCoin, cardId, laneId, unlockCoinPrice, toast, context.credential.user])

  if (superLaneUnlockByCoin) {
    return (
      <span className="badge bg-info">
        コイン解放済み ({superLaneUnlockByCoin.coins_charged}コイン)
      </span>
    )
  }

  return (
    <button
      className="btn btn-warning btn-sm"
      onClick={handleUnlock}
      disabled={isSubmitting}
    >
      {isSubmitting ? "処理中..." : `スーパーレーン解放 (${unlockCoinPrice}コイン)`}
    </button>
  )
}

type StampLaneRewardsDisplayProps = {
  cardId: StampCardId
  lane: StampLaneDocument
  rewards: StampLaneRewardDocument[]
  claims: UserStampRewardClaimDocument[]
  claimPeriodStart: number | null
  claimPeriodEnd: number | null
  currentStampCount: number
}

const StampLaneRewardsDisplay = ({ cardId, lane, rewards, claims, claimPeriodStart, claimPeriodEnd, currentStampCount }: StampLaneRewardsDisplayProps) => {
  const claimedRewardIds = useMemo(() => {
    return new Set(claims.map(c => c.id))
  }, [claims])

  const sortedRewards = useMemo(() => {
    return [...rewards].sort(sortBy(r => r.threshold_stamps))
  }, [rewards])

  if (sortedRewards.length === 0) {
    return null
  }

  const isSuper = isSuperLane(lane)

  return (
    <div className="mt-3">
      <h6>
        報酬
        {isSuper && (
          <span className="badge bg-warning text-dark ms-2">スーパーレーン</span>
        )}
      </h6>
      <div className="small text-muted mb-2">
        受取可能期間: {formatDateOrIndefinite(claimPeriodStart)} 〜 {formatDateOrIndefinite(claimPeriodEnd)}
      </div>
      <ul className="list-group list-group-flush">
        {sortedRewards.map(reward => (
          <StampLaneRewardItem
            key={reward.id}
            cardId={cardId}
            laneId={lane.id}
            reward={reward}
            isClaimed={claimedRewardIds.has(reward.id)}
            currentStampCount={currentStampCount}
          />
        ))}
      </ul>
    </div>
  )
}

type StampLaneWithRewardsProps = {
  cardId: StampCardId
  lane: StampLaneDocument
  userId: UserId
  claimPeriodStart: number | null
  claimPeriodEnd: number | null
  currentStampCount: number
  superLaneUnlockByCoin: UserStampCardStateSuperLaneUnlockByCoin | undefined
}

const StampLaneWithRewards = ({ cardId, lane, userId, claimPeriodStart, claimPeriodEnd, currentStampCount, superLaneUnlockByCoin }: StampLaneWithRewardsProps) => {
  const rewards = useStampLaneRewards(firestore, cardId, lane.id)
  const claims = useUserStampRewardClaims(firestore, userId, cardId, lane.id)

  if (rewards === "loading" || claims === "loading") {
    return <SpinnerRow />
  }

  const isSuper = isSuperLane(lane)
  const unlockCoinPrice = lane.unlockable_by.coin ? lane.unlock_coin_price : undefined

  return (
    <div className="mt-3">
      {isSuper && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="badge bg-secondary">スーパーレーン</span>
          {unlockCoinPrice != null && (
            <SuperLaneUnlockButton
              cardId={cardId}
              laneId={lane.id}
              unlockCoinPrice={unlockCoinPrice}
              superLaneUnlockByCoin={superLaneUnlockByCoin}
            />
          )}
        </div>
      )}
      <StampLaneRewardsDisplay
        cardId={cardId}
        lane={lane}
        rewards={rewards}
        claims={claims}
        claimPeriodStart={claimPeriodStart}
        claimPeriodEnd={claimPeriodEnd}
        currentStampCount={currentStampCount}
      />
    </div>
  )
}

type StampCardRewardsProps = {
  cardId: StampCardId
  userId: UserId
  claimPeriodStart: number | null
  claimPeriodEnd: number | null
  currentStampCount: number
  superLaneUnlockByCoin: UserStampCardStateSuperLaneUnlockByCoin | undefined
}

const StampCardRewards = ({ cardId, userId, claimPeriodStart, claimPeriodEnd, currentStampCount, superLaneUnlockByCoin }: StampCardRewardsProps) => {
  const lanes = useStampLanes(firestore, cardId)

  if (lanes === "loading") {
    return <SpinnerRow />
  }

  if (lanes.length === 0) {
    return null
  }

  return (
    <>
      {lanes.map(lane => (
        <StampLaneWithRewards
          key={lane.id}
          cardId={cardId}
          lane={lane}
          userId={userId}
          claimPeriodStart={claimPeriodStart}
          claimPeriodEnd={claimPeriodEnd}
          currentStampCount={currentStampCount}
          superLaneUnlockByCoin={superLaneUnlockByCoin}
        />
      ))}
    </>
  )
}

export type StampCardViewData = {
  card: StampCardDocument
  state: UserStampCardStateDocument | undefined
  missions: StampMissionDocument[]
  progresses: Map<string, UserStampMissionProgressDocument>
  userId: UserId
}

type StampCardDetailViewProps = {
  data: StampCardViewData
}

export const StampCardDetailView = ({ data }: StampCardDetailViewProps) => {
  const { card, state, missions, progresses, userId } = data

  const sortedMissions = useMemo(() => {
    return [...missions].sort(sortBy(m => -m.priority))
  }, [missions])

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <strong>{card.name}</strong>
          <span className="badge bg-success ms-2">有効</span>
        </div>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <strong>獲得スタンプ数:</strong> {state?.stamp_count ?? 0}
        </div>

        <h6>ミッション進捗</h6>
        {sortedMissions.length === 0 ? (
          <div className="alert alert-info mb-0">ミッションがありません</div>
        ) : (
          <ul className="list-group list-group-flush">
            {sortedMissions.map(mission => (
              <li key={mission.id} className="list-group-item px-0">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
                  <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                    <span className="fw-bold text-break">{mission.name}</span>
                    <span className="text-muted small ms-2">
                      {mission.repeat_kind === "daily" ? "デイリー" : "限定"} / {getMissionKindLabel(mission.kind)}
                      {"time_scope" in mission && ` / ${getTimeScopeLabel(mission.time_scope)}`}
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-1 small text-muted flex-wrap gap-1">
                  <MissionProgressDisplay
                    mission={mission}
                    progress={progresses.get(mission.id)}
                    userId={userId}
                    cardId={card.id}
                    missionPeriodStartAt={card.mission_period.start_at}
                  />
                  <span className="flex-shrink-0">スタンプ: {mission.award_stamps}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export type UserStampCardsViewProps= {
  cards: StampCardViewData[]
}

export const UserStampCardsView = ({ cards }: UserStampCardsViewProps) => {
  if (cards.length === 0) {
    return (
      <div className="alert alert-info">
        現在有効なスタンプカードがありません。
      </div>
    )
  }

  return (
    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 w-100">
      {cards.map(data => (
        <div key={data.card.id} className="col">
          <StampCardDetailView data={data} />
        </div>
      ))}
    </div>
  )
}

type StampCardDetailProps = {
  card: StampCardDocument
  state: UserStampCardStateDocument | undefined
  userId: UserId
}

const StampCardDetail = ({ card, state, userId }: StampCardDetailProps) => {
  const missions = useReadableStampMissions(firestore, card.id)
  const progresses = useUserStampMissionProgresses(firestore, userId, card.id)

  const progressMap = useMemo(() => {
    if (progresses === "loading") return new Map<string, UserStampMissionProgressDocument>()
    return new Map(progresses.map(p => [p.id, p]))
  }, [progresses])

  const sortedMissions = useMemo(() => {
    if (missions === "loading") return []
    return [...missions].sort(sortBy(m => -m.priority))
  }, [missions])

  if (missions === "loading" || progresses === "loading") {
    return (
      <div className="card h-100">
        <div className="card-header">
          <strong>{card.name}</strong>
          <span className="badge bg-success ms-2">有効</span>
        </div>
        <div className="card-body">
          <SpinnerRow />
        </div>
      </div>
    )
  }

  const currentStampCount = state?.stamp_count ?? 0

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <strong>{card.name}</strong>
          <span className="badge bg-success ms-2">有効</span>
        </div>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <strong>獲得スタンプ数:</strong> {currentStampCount}
        </div>

        <h6>ミッション進捗</h6>
        {sortedMissions.length === 0 ? (
          <div className="alert alert-info mb-0">ミッションがありません</div>
        ) : (
          <ul className="list-group list-group-flush">
            {sortedMissions.map(mission => (
              <li key={mission.id} className="list-group-item px-0">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
                  <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                    <span className="fw-bold text-break">{mission.name}</span>
                    <span className="text-muted small ms-2">
                      {mission.repeat_kind === "daily" ? "デイリー" : "限定"} / {getMissionKindLabel(mission.kind)}
                      {"time_scope" in mission && ` / ${getTimeScopeLabel(mission.time_scope)}`}
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-1 small text-muted flex-wrap gap-1">
                  <MissionProgressDisplay
                    mission={mission}
                    progress={progressMap.get(mission.id)}
                    userId={userId}
                    cardId={card.id}
                    missionPeriodStartAt={card.mission_period.start_at}
                  />
                  <span className="flex-shrink-0">スタンプ: {mission.award_stamps}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="d-flex justify-content-end mt-3 mb-2">
          <BulkClaimButton cardId={card.id} />
        </div>

        <StampCardRewards
          cardId={card.id}
          userId={userId}
          claimPeriodStart={card.mission_period.end_at}
          claimPeriodEnd={card.claim_period.end_at}
          currentStampCount={currentStampCount}
          superLaneUnlockByCoin={state?.super_lane_unlock_by_coin}
        />
      </div>
    </div>
  )
}

type UserStampCardsProps = {
  userId: UserId
}

export const UserStampCards = ({ userId }: UserStampCardsProps) => {
  const stampCards = useReadableStampCards(firestore)
  const userStampCardStates = useUserStampCardStates(firestore, userId)

  const stateMap = useMemo(() => {
    if (userStampCardStates === "loading") return new Map<StampCardId, UserStampCardStateDocument>()
    return new Map(userStampCardStates.map(s => [s.card_id as StampCardId, s]))
  }, [userStampCardStates])

  const sortedCards = useMemo(() => {
    if (stampCards === "loading") return []
    return [...stampCards].sort(sortBy(c => [-c.priority, -c.created_at]))
  }, [stampCards])

  if (stampCards === "loading" || userStampCardStates === "loading") {
    return <SpinnerRow />
  }

  if (sortedCards.length === 0) {
    return (
      <div className="alert alert-info">
        現在有効なスタンプカードがありません。
      </div>
    )
  }

  return (
    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 w-100">
      {sortedCards.map(card => (
        <div key={card.id} className="col">
          <StampCardDetail
            card={card}
            state={stateMap.get(card.id)}
            userId={userId}
          />
        </div>
      ))}
    </div>
  )
}
